import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const SCHEMA_CACHE_TIME = 30 * 60 * 1000; // 30 minutes

export function useDatabases() {
  return useQuery({
    queryKey: ['databases'],
    queryFn: async () => {
      const response = await api.get('/databases');
      return response.data;
    },
    staleTime: SCHEMA_CACHE_TIME,
    cacheTime: SCHEMA_CACHE_TIME * 2,
  });
}

export function useDatabaseSchema(databaseId) {
  return useQuery({
    queryKey: ['databases', databaseId],
    queryFn: async () => {
      const response = await api.get(`/databases/${databaseId}`);
      return response.data;
    },
    enabled: !!databaseId,
    staleTime: SCHEMA_CACHE_TIME,
    cacheTime: SCHEMA_CACHE_TIME * 2,
  });
}

export function useCreateDatabase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (schema) => {
      const response = await api.post('/databases', schema);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    },
  });
}

export function useUpdateDatabase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const response = await api.put(`/databases/${id}`, updates);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['databases', data.id] });
      }
    },
  });
}

export function useDeleteDatabase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/databases/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    },
  });
}

export function useInferCSVSchema() {
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('csvFile', file);
      const response = await api.post('/databases/infer-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });
}

export function useInferCSVPair() {
  return useMutation({
    mutationFn: async ({ preFile, postFile }) => {
      const formData = new FormData();
      formData.append('preFile', preFile);
      formData.append('postFile', postFile);
      const response = await api.post('/databases/infer-csv-pair', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });
}
